"use client";

/**
 * Landing Page - Main Container Component
 * Orchestrates all landing page sections with reCAPTCHA integration
 */

import { useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { siteConfig } from "@/config/site";
import { Navigation } from "./Navigation";
import { HeroSection } from "./HeroSection";
import { FeaturesSection } from "./FeaturesSection";
import { ContactSection } from "./ContactSection";
import { Footer } from "./Footer";

export function LandingPage() {
  const reCaptchaRef = useRef<ReCAPTCHA | null>(null);

  /**
   * Execute reCAPTCHA and return token
   * Returns empty string if reCAPTCHA is disabled
   * Returns null if reCAPTCHA fails
   */
  const checkCaptcha = async (): Promise<string | null> => {
    if (!siteConfig.isRecaptchaEnabled) {
      return "";
    }

    const recaptchaToken = await reCaptchaRef.current?.executeAsync();

    if (typeof recaptchaToken !== "string" || recaptchaToken.length === 0) {
      return null;
    }

    // Reset reCAPTCHA for next use
    reCaptchaRef.current?.reset();

    return recaptchaToken;
  };

  return (
    <div className="font-sans relative">
      <div className="h-screen overflow-y-auto scroll-smooth">
        <div id="top">
          <Navigation />
          <HeroSection checkCaptcha={checkCaptcha} />
          <FeaturesSection />
          <ContactSection />
          <Footer checkCaptcha={checkCaptcha} />

          {/* Invisible reCAPTCHA */}
          {siteConfig.isRecaptchaEnabled && (
            <ReCAPTCHA
              ref={reCaptchaRef}
              size="invisible"
              sitekey={siteConfig.recaptchaSiteKey}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default LandingPage;
